<script setup lang="ts">
/*
 * The "Direct" group caption + "Direct Workforce" item the prototype renders
 * under every company in the scope menu (`renderScopeMenu`, L15626–15630):
 *
 *   <div class="sm-grp">Direct</div>
 *   <div class="sm-item sm-direct[ active]" data-scope="cdirect:<companyId>">
 *     <i data-lucide="users"></i>
 *     <div class="sm-txt"><span class="sm-name">Direct Workforce</span></div>
 *     [<i data-lucide="check" class="sm-check"></i>]
 *   </div>
 *
 * Classes are kept verbatim (`sm-grp`, `sm-item`, `sm-direct`, `sm-txt`,
 * `sm-name`, `sm-check`); their prototype declarations (L4436–4453) are ported
 * into `org-chip-tree.css` under the menu's test id. The item is a Headless UI
 * `MenuItem` so arrow keys / Enter / Esc work like every other scope row, and
 * it exposes its selected state through `aria-current`.
 *
 * Pure chrome: it emits `select`; the parent owns the scope change.
 */
import { MenuItem } from '@headlessui/vue';
import { Check, Users } from 'lucide-vue-next';
import { useI18n } from 'vue-i18n';
import './org-chip-tree.css';

const { t } = useI18n();

defineProps<{
  /** The COMPANY whose Direct Workforce this item selects. */
  companyId: string;
  /** `true` when this company's Direct Workforce is the active scope. */
  selected: boolean;
}>();

const emit = defineEmits<{ (e: 'select'): void }>();
</script>

<template>
  <p class="sm-grp" :data-testid="`org-chip-group-direct-${companyId}`">
    {{ t('v2.shell.org-chip.direct') }}
  </p>
  <MenuItem v-slot="{ active }">
    <button
      type="button"
      :class="['sm-item', 'sm-direct', { active: selected, 'sm-focus': active && !selected }]"
      :data-scope="`cdirect:${companyId}`"
      :data-testid="`org-chip-option-direct-${companyId}`"
      :aria-current="selected ? 'true' : undefined"
      @click="emit('select')"
    >
      <Users aria-hidden="true" />
      <span class="sm-txt">
        <span class="sm-name">{{ t('v2.shell.org-chip.direct-workforce') }}</span>
      </span>
      <Check v-if="selected" class="sm-check" aria-hidden="true" />
    </button>
  </MenuItem>
</template>
